/**
 * Retail Routes Configuration - OPTIMIZED WITH LAZY LOADING
 * Branch, sales, inventory, products, customers
 * All 44 pages are lazy-loaded for massive bundle size reduction
 */
import React from 'react';
import { Route } from 'react-router-dom';
import ProtectedRoute from '../components/ProtectedRoute';

// LAZY LOAD all page components (44 pages)
// Category Management
import CategoriesPage from '../pages/CategoriesPage';
import BrandsPage from '../pages/BrandsPage';
import SizesPage from '../pages/SizesPage';
import UseCasesPage from '../pages/UseCasesPage';
import TileTypesPage from '../pages/TileTypesPage';
import OptionalCategoriesPage from '../pages/OptionalCategoriesPage';
import AccessoryCategoriesPage from '../pages/AccessoryCategoriesPage';

// Product Management
import ProductsPage from '../pages/ProductsPage';
import ProductFormPage from '../pages/ProductFormPage';
import ProductDetailPage from '../pages/ProductDetailPage';
import ProductCataloguePage from '../pages/ProductCataloguePage';
import StockDamagePage from '../pages/StockDamagePage';
import InactiveProductsPage from '../pages/InactiveProductsPage';

// Customer Management
import CustomersPage from '../pages/CustomersPage';
import CustomerFormPage from '../pages/CustomerFormPage';
import CustomerDetailPage from '../pages/CustomerDetailPage';

// Referrer Management
import ReferrersPage from '../pages/ReferrersPage';
import ReferrerFormPage from '../pages/ReferrerFormPage';
import ReferrerDetailPage from '../pages/ReferrerDetailPage';
import CommissionPaymentsPage from '../pages/CommissionPaymentsPage';

// Sales Management
import SalesList from '../pages/sales/SalesList';
import SalesForm from '../pages/sales/SalesForm';
import SaleDetail from '../pages/sales/SaleDetail';
import SalesAnalytics from '../pages/sales/SalesAnalytics';
import PendingConfirmationsPage from '../pages/sales/PendingConfirmationsPage';
import CancelledSalesPage from '../pages/sales/CancelledSalesPage';

// Receipt Management
import ReceiptHistory from '../pages/receipts/ReceiptHistory';
import ReceiptSettings from '../pages/receipts/ReceiptSettings';
import WhatsAppHistoryPage from '../pages/WhatsAppHistoryPage';

// Branch Management
import BranchesPage from '../pages/branches/BranchesPage';
import BranchDetailPage from '../pages/branches/BranchDetailPage';
import BranchInventoryPage from '../pages/branches/BranchInventoryPage';
import MyBranchInventoryPage from '../pages/branches/MyBranchInventoryPage';
import MyBranchPage from '../pages/branches/MyBranchPage';
import BranchDashboard from '../pages/branches/BranchDashboard';
import ConsolidatedDashboard from '../pages/branches/ConsolidatedDashboard';

// Stock Transfers
import StockTransfersPage from '../pages/inventory/StockTransfersPage';
import TransferDetailPage from '../pages/inventory/TransferDetailPage';

// Tile Calculator
import TileCalculatorPage from '../pages/calculator/TileCalculatorPage';

// Wholesaler Credit (Legacy)
import WholesalersPage from '../pages/WholesalersPage';
import WholesalerDetailPage from '../pages/WholesalerDetailPage';

// Performance Tasks
import ActiveTasksPage from '../pages/retail/performance-tasks/ActiveTasksPage';
import InactiveTasksPage from '../pages/retail/performance-tasks/InactiveTasksPage';
import TaskDetailPage from '../pages/retail/performance-tasks/TaskDetailPage';
import MyTasksPage from '../pages/retail/performance-tasks/MyTasksPage';

const adminRoles = ['admin', 'super_admin', 'system_admin', 'retail_admin'];
const branchRoles = ['admin', 'super_admin', 'system_admin', 'retail_admin', 'branch_admin'];
const allBranchRoles = [
  'admin',
  'super_admin',
  'system_admin',
  'retail_admin',
  'branch_admin',
  'branch_staff'
];

/**
 * Retail module routes - Lazy-loaded components
 */
export const retailRoutes = (
  <>
    {/* Category Management - Admin Only */}
    <Route
      path="/categories"
      element={
        <ProtectedRoute allowedRoles={adminRoles}>
          <CategoriesPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/categories/brands"
      element={
        <ProtectedRoute allowedRoles={adminRoles}>
          <BrandsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/categories/sizes"
      element={
        <ProtectedRoute allowedRoles={adminRoles}>
          <SizesPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/categories/use-cases"
      element={
        <ProtectedRoute allowedRoles={adminRoles}>
          <UseCasesPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/categories/tile-types"
      element={
        <ProtectedRoute allowedRoles={adminRoles}>
          <TileTypesPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/categories/optional"
      element={
        <ProtectedRoute allowedRoles={adminRoles}>
          <OptionalCategoriesPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/accessory-categories"
      element={
        <ProtectedRoute allowedRoles={adminRoles}>
          <AccessoryCategoriesPage />
        </ProtectedRoute>
      }
    />

    {/* Product Management */}
    <Route path="/catalogue" element={<ProductCataloguePage />} />
    <Route path="/products" element={<ProductsPage />} />
    <Route path="/products/new" element={<ProductFormPage />} />
    <Route path="/products/:id" element={<ProductDetailPage />} />
    <Route path="/products/:id/edit" element={<ProductFormPage />} />
    <Route path="/inactive-products" element={<InactiveProductsPage />} />
    <Route path="/stock-damages" element={<StockDamagePage />} />

    {/* Customer Management */}
    <Route path="/customers" element={<CustomersPage />} />
    <Route path="/customers/new" element={<CustomerFormPage />} />
    <Route path="/customers/:id" element={<CustomerDetailPage />} />
    <Route path="/customers/:id/edit" element={<CustomerFormPage />} />

    {/* Referrer Management */}
    <Route path="/referrers" element={<ReferrersPage />} />
    <Route path="/referrers/new" element={<ReferrerFormPage />} />
    <Route path="/referrers/:id" element={<ReferrerDetailPage />} />
    <Route path="/referrers/:id/edit" element={<ReferrerFormPage />} />
    <Route
      path="/commissions"
      element={
        <ProtectedRoute allowedRoles={branchRoles}>
          <CommissionPaymentsPage />
        </ProtectedRoute>
      }
    />

    {/* Sales Management */}
    <Route path="/sales" element={<SalesList />} />
    <Route path="/sales/new" element={<SalesForm />} />
    <Route path="/sales/analytics" element={<SalesAnalytics />} />
    <Route
      path="/sales/pending-confirmations"
      element={
        <ProtectedRoute allowedRoles={branchRoles}>
          <PendingConfirmationsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/sales/cancelled"
      element={
        <ProtectedRoute allowedRoles={branchRoles}>
          <CancelledSalesPage />
        </ProtectedRoute>
      }
    />
    <Route path="/sales/:id" element={<SaleDetail />} />

    {/* Receipt Management */}
    <Route path="/receipts" element={<ReceiptHistory />} />
    <Route path="/receipts/settings" element={<ReceiptSettings />} />
    <Route path="/receipts/whatsapp-history" element={<WhatsAppHistoryPage />} />

    {/* Branch Management */}
    <Route
      path="/branches"
      element={
        <ProtectedRoute allowedRoles={branchRoles}>
          <BranchesPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/branches/:id"
      element={
        <ProtectedRoute allowedRoles={branchRoles}>
          <BranchDetailPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/branches/:branchId/inventory"
      element={
        <ProtectedRoute allowedRoles={allBranchRoles}>
          <BranchInventoryPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/my-branch-inventory"
      element={
        <ProtectedRoute allowedRoles={['retail_admin', 'branch_admin', 'branch_staff']}>
          <MyBranchInventoryPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/my-branch"
      element={
        <ProtectedRoute allowedRoles={['retail_admin', 'branch_admin']}>
          <MyBranchPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/branches/:branchId/dashboard"
      element={
        <ProtectedRoute allowedRoles={branchRoles}>
          <BranchDashboard />
        </ProtectedRoute>
      }
    />
    <Route
      path="/analytics/consolidated"
      element={
        <ProtectedRoute allowedRoles={['super_admin', 'system_admin', 'retail_admin']}>
          <ConsolidatedDashboard />
        </ProtectedRoute>
      }
    />

    {/* Stock Transfers */}
    <Route
      path="/stock-transfers"
      element={
        <ProtectedRoute allowedRoles={branchRoles}>
          <StockTransfersPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/stock-transfers/:id"
      element={
        <ProtectedRoute allowedRoles={branchRoles}>
          <TransferDetailPage />
        </ProtectedRoute>
      }
    />

    {/* Tile Calculator */}
    <Route path="/calculator" element={<TileCalculatorPage />} />

    {/* Wholesaler Credit (Legacy) */}
    <Route path="/wholesalers" element={<WholesalersPage />} />
    <Route path="/wholesalers/:id" element={<WholesalerDetailPage />} />

    {/* Performance Tasks */}
    <Route
      path="/performance-tasks/active"
      element={
        <ProtectedRoute allowedRoles={adminRoles}>
          <ActiveTasksPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/performance-tasks/inactive"
      element={
        <ProtectedRoute allowedRoles={adminRoles}>
          <InactiveTasksPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/performance-tasks/:id"
      element={
        <ProtectedRoute allowedRoles={allBranchRoles}>
          <TaskDetailPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/my-performance-tasks"
      element={
        <ProtectedRoute allowedRoles={allBranchRoles}>
          <MyTasksPage />
        </ProtectedRoute>
      }
    />
  </>
);

export default retailRoutes;
